import React, { useLayoutEffect, useMemo } from "react";
import "./tax-report-print.css";

import PrintReportDashboard from "./PrintReportDashboard";
import {
  buildUkFinalExport,
  isUkFinalTokenTableRowExcluded,
} from "../utils/ukFinalExport.js";
import VetTaxTable from "../components/VetTaxTable";
import VthoTaxTable from "../components/VthoTaxTable";
import B3trTaxTable from "../components/B3trTaxTable";
import LiquidityTaxTable from "../components/LiquidityTaxTable";
import NftTaxTable from "../components/NftTaxTable";
import ExchangeImportsTable from "../components/ExchangeImportsTable";
import RawTransfersTable from "../components/RawTransfersTable";
import {
  formatTaxYearLabel,
  getTaxYearPeriod,
  isTaxYearOpen,
} from "../utils/taxYear.js";

function asNumber(v) {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n : 0;
}

const noop = () => {};


function pickMoney(totals = {}, ...keys) {
  for (const key of keys) {
    const value = totals?.[key];
    if (value !== null && value !== undefined && value !== "") {
      const n = Number(value);
      if (Number.isFinite(n)) return n;
    }
  }
  return 0;
}

function withReportModeTotals(totals = {}, reportMode = "full") {
  if (reportMode === "income") {
    return {
      ...totals,
      disposalNetEUR: 0,
      disposalNetFiat: 0,
      disposalProfitEUR: 0,
      disposalProfitFiat: 0,
      disposalEUR: 0,
      disposalFiat: 0,
    };
  }

  if (reportMode === "disposal") {
    return {
      ...totals,
      otherIncomeEUR: 0,
      otherIncomeFiat: 0,
      incomeEUR: 0,
      incomeFiat: 0,
    };
  }

  return totals || {};
}

function normalizedClass(row = {}) {
  return String(row.classification || row.type || row.kind || row.event || "")
    .trim()
    .toLowerCase();
}

function rowLooksIncome(row = {}) {
  const cls = normalizedClass(row);
  const hasFifoLegs = Array.isArray(row.fifoLegs) && row.fifoLegs.length > 0;
  const hasDisposalShape =
    hasFifoLegs ||
    row.grossBuyFiat != null ||
    row.grossBuyEUR != null ||
    row.grossSellFiat != null ||
    row.grossSellEUR != null ||
    row.grossProfitFiat != null ||
    row.grossProfitEUR != null;

  if (hasDisposalShape) return false;

  return (
    cls === "generated" ||
    cls.includes("income") ||
    cls.includes("reward") ||
    cls.includes("airdrop") ||
    cls.includes("staking") ||
    cls.includes("claim") ||
    row.otherIncomeFiat != null ||
    row.otherIncomeEUR != null ||
    row.incomeFiat != null ||
    row.incomeEUR != null
  );
}

function rowLooksDisposal(row = {}) {
  const cls = normalizedClass(row);

  return (
    (Array.isArray(row.fifoLegs) && row.fifoLegs.length > 0) ||
    cls.includes("swap_out") ||
    cls.includes("sale") ||
    cls.includes("mint") ||
    cls.includes("nfbc_purchase") ||
    cls.includes("liquidity") ||
    cls.includes("exchange_trade_sell") ||
    row.grossBuyFiat != null ||
    row.grossBuyEUR != null ||
    row.grossSellFiat != null ||
    row.grossSellEUR != null ||
    row.grossProfitFiat != null ||
    row.grossProfitEUR != null
  );
}

function filterRowsForReport(rows = [], reportMode = "full") {
  const list = Array.isArray(rows) ? rows : [];
  if (reportMode === "income") return list.filter(rowLooksIncome);
  if (reportMode === "disposal") return list.filter(rowLooksDisposal);
  return list;
}

function filterExchangeImportsForReport(rows = [], reportMode = "full") {
  const list = Array.isArray(rows) ? rows : [];
  if (reportMode === "income") {
    return list.filter((row) => {
      const kind = String(row.kind || row.type || "").toLowerCase();
      return ["income", "staking", "reward", "interest", "airdrop"].includes(kind);
    });
  }
  if (reportMode === "disposal") {
    return list.filter((row) => {
      const kind = String(row.kind || row.type || "").toLowerCase();
      const side = String(row.side || "").toUpperCase();
      return kind === "trade" && side === "SELL";
    });
  }
  return list;
}

function FactRows({ rows }) {
  return (
    <dl className="tr-fact-list">
      {rows.map((row) => (
        <React.Fragment key={row.label}>
          <dt>{row.label}</dt>
          <dd>{row.value ?? "-"}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function normalizeCountryCode(country = "DE") {
  return String(country || "DE").trim().toUpperCase().replace(/\s+/g, "_");
}

function isUkCountry(country = "") {
  const cc = normalizeCountryCode(country);
  return cc === "UK" || cc === "GB" || cc === "UNITED_KINGDOM";
}

function expectedCurrencyForCountry(country = "DE") {
  const cc = normalizeCountryCode(country);
  if (cc === "US" || cc === "USA" || cc === "UNITED_STATES") return "USD";
  if (isUkCountry(cc)) return "GBP";
  if (cc === "CH" || cc === "CHE" || cc === "SWITZERLAND") return "CHF";
  return "EUR";
}

function expectedMethodForCountry(country = "DE") {
  const cc = normalizeCountryCode(country);
  if (isUkCountry(cc)) return "UK Section 104 pooling";
  if (cc === "IT") return "LIFO";
  return "FIFO";
}

function pairForReportMode(pair = {}, reportMode = "full") {
  const swapProfit = asNumber(pair?.swapProfit);
  const otherIncome = asNumber(pair?.otherIncome);

  return {
    swapProfit: reportMode === "income" ? 0 : swapProfit,
    otherIncome: reportMode === "disposal" ? 0 : otherIncome,
    total:
      (reportMode === "income" ? 0 : swapProfit) +
      (reportMode === "disposal" ? 0 : otherIncome),
  };
}

function filterUkRowsForPdf(rows = [], isUK = false) {
  const list = Array.isArray(rows)
    ? rows
    : [];

  if (!isUK) {
    return list;
  }

  return list.filter(
    (row) => !isUkFinalTokenTableRowExcluded(row)
  );
}

function filterUkLiquidityRowsForPdf(rows = [], isUK = false) {
  const list = Array.isArray(rows) ? rows : [];
  if (!isUK) return list;

  return list
    .map((row) => {
      if (isUkFinalTokenTableRowExcluded(row)) return null;

      if (Array.isArray(row?.disposals)) {
        const disposals = row.disposals.filter(
          (d) => !isUkFinalTokenTableRowExcluded(d)
        );

        if (!disposals.length) return null;
        return { ...row, disposals };
      }

      return row;
    })
    .filter(Boolean);
}

export default function TaxReportPrint({
  language = "en",
  pdfVariant = "calc",
  datasetId,
  finalYear,
  selectedCountry,
  walletSet,
  backendData,
  manualClassifications = {},
  nonTaxableSales = {},
  manualNftData = {},
  handleNonTaxableSaleChange = noop,
  handleManualNftDataChange = noop,
  rawVetVthoTxs = [],
  rawVip180Txs = [],
  loadFromSnapshot = false,
}) {
  const ds = backendData?.analysisResult?.dataset || backendData?.dataset || {};
  const meta = backendData?.meta || {};
  const cc = normalizeCountryCode(meta.countryCode || selectedCountry || "DE");
  const isUK = isUkCountry(cc);
  const displayTaxYear = formatTaxYearLabel(finalYear, cc);

  const reportMode =
    pdfVariant === "income" ? "income" :
    pdfVariant === "disposal" ? "disposal" :
    "full";
  const isCalcLike = ["calc", "income", "disposal"].includes(pdfVariant);
  const isIncomeReport = reportMode === "income";
  const isDisposalReport = reportMode === "disposal";

  const reportSuffix =
    pdfVariant === "raw" ? "A2" :
    pdfVariant === "income" ? "I1" :
    pdfVariant === "disposal" ? "D1" :
    "A1";
  const reportTitle =
    pdfVariant === "income"
      ? language === "en"
        ? `Income Report ${displayTaxYear}`
        : `Einkünfte-Report ${displayTaxYear}`
      : pdfVariant === "disposal"
        ? language === "en"
          ? `Disposal Report ${displayTaxYear}`
          : `Veräußerungs-Report ${displayTaxYear}`
        : language === "en"
          ? `Tax Report ${displayTaxYear}`
          : `Steuerreport ${displayTaxYear}`;

  const reportSubtitle =
    pdfVariant === "raw"
      ? language === "en"
        ? "APPENDIX 2 – CLASSIFIED LEDGER REPORT"
        : "ANLAGE 2 – KLASSIFIZIERTES LEDGER REPORT"
      : pdfVariant === "income"
        ? language === "en"
          ? "INCOME REPORT – TAXABLE INCOME DETAILS"
          : "EINKÜNFTE-REPORT – DETAILS DER STEUERRELEVANTEN EINKÜNFTE"
        : pdfVariant === "disposal"
          ? language === "en"
            ? "DISPOSAL REPORT – CAPITAL GAINS / LOSSES DETAILS"
            : "VERÄUSSERUNGS-REPORT – DETAILS ZU GEWINNEN / VERLUSTEN"
          : language === "en"
            ? "APPENDIX 1 – CALCULATION REPORT"
            : "ANLAGE 1 – BERECHNUNGSREPORT";

  const coverLead =
    pdfVariant === "raw"
      ? language === "en"
        ? "Classified ledger appendix for the selected tax year."
        : "Klassifizierte Ledger-Anlage für das ausgewählte Steuerjahr."
      : pdfVariant === "income"
        ? language === "en"
          ? "Income-only report for accountant review. Disposal details are intentionally excluded."
          : "Separater Einkünfte-Report für die Beraterprüfung. Veräußerungsdetails werden bewusst nicht ausgegeben."
        : pdfVariant === "disposal"
          ? language === "en"
            ? "Disposal-only report for accountant review. Income details are intentionally excluded."
            : "Separater Veräußerungs-Report für die Beraterprüfung. Einkünfte werden bewusst nicht ausgegeben."
          : language === "en"
            ? "Structured calculation overview for the selected tax year."
            : "Strukturierte Berechnungsübersicht für das ausgewählte Steuerjahr.";

  const reportYearNum = Number(finalYear);
  const now = new Date();
  const reportAsOfTs = Math.floor(now.getTime() / 1000);
  const taxYearPeriod = Number.isFinite(reportYearNum)
    ? getTaxYearPeriod(cc, reportYearNum)
    : null;
  const reportEndTs = taxYearPeriod
    ? Math.min(reportAsOfTs, taxYearPeriod.endInclusiveTs)
    : reportAsOfTs;
  const isPartialOpenTaxYear = Number.isFinite(reportYearNum)
    ? isTaxYearOpen(cc, reportYearNum, reportAsOfTs)
    : false;
  const fmtUtcDate = (tsSec) =>
    new Intl.DateTimeFormat(language === "de" ? "de-DE" : "en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: "UTC",
    }).format(new Date(Number(tsSec) * 1000));
  const dataAsOfDate = fmtUtcDate(reportEndTs);
  const reportingStartDate = taxYearPeriod
    ? fmtUtcDate(taxYearPeriod.startTs)
    : isUK
      ? fmtUtcDate(Date.UTC(reportYearNum, 3, 6) / 1000)
      : `01.01.${reportYearNum}`;
  const partialYearNotice = isPartialOpenTaxYear
    ? language === "en"
      ? `Data as of ${dataAsOfDate} – provisional report for the open tax year (reporting period ${reportingStartDate} to ${dataAsOfDate}).`
      : `Datenstand: ${dataAsOfDate} – vorläufiger Bericht für das laufende Steuerjahr (Berichtszeitraum ${reportingStartDate} bis ${dataAsOfDate}).`
    : null;

  const priceSourceLabel = (() => {
    const pm =
      ds?.pricingMethodology ||
      meta?.pricingMethodology ||
      null;
    if (pm?.publicLabelDe || pm?.publicLabelEn) {
      return language === "en"
        ? pm.publicLabelEn || pm.publicLabelDe
        : pm.publicLabelDe || pm.publicLabelEn;
    }
    return language === "en"
      ? "Historical transaction-time pricing data"
      : "Historische, transaktionszeitbezogene Kursdaten";
  })();

  const pricingMethodologyHeader = (() => {
    const pm = ds?.pricingMethodology || meta?.pricingMethodology || null;
    if (pm?.publicHeaderDe || pm?.publicHeaderEn) {
      return language === "en"
        ? pm.publicHeaderEn || pm.publicHeaderDe
        : pm.publicHeaderDe || pm.publicHeaderEn;
    }
    return priceSourceLabel;
  })();

  const pricingMethodologyNote = (() => {
    const pm = ds?.pricingMethodology || meta?.pricingMethodology || null;
    if (!pm) return null;
    return language === "en"
      ? pm.publicNoteEn || pm.publicNoteDe
      : pm.publicNoteDe || pm.publicNoteEn;
  })();

  const methodUsed = isUK
    ? expectedMethodForCountry(cc)
    : meta.costBasisMethod || expectedMethodForCountry(cc);

  const currencyUsed = String(
    ds?.pricingMethodology?.reportCurrency ||
      meta.fiatCurrency ||
      (isUK ? "GBP" : expectedCurrencyForCountry(cc)) ||
      ""
  ).toUpperCase();

  const usWalletMethodNote =
    String(cc || "").toUpperCase() === "US"
      ? language === "en"
        ? "Cost basis is maintained separately for each wallet or account. Historical lots are assigned to the wallet or account in which they originated. Users who previously applied a different cross-wallet method should review their opening allocation."
        : "Die Kostenbasis wird je Wallet bzw. Konto geführt. Historische Lots werden der Wallet oder dem Konto zugeordnet, in dem sie entstanden sind. Nutzer, die zuvor eine abweichende walletübergreifende Methode verwendet haben, sollten ihre Eröffnungszuordnung prüfen."
      : null;

  const lotScopeUsed = String(
    meta.lotScope ||
      ds?.lotScope ||
      (String(cc || "").toUpperCase() === "IT"
        ? "GLOBAL_TOKEN_POOL"
        : String(cc || "").toUpperCase() === "US" ||
            String(cc || "").toUpperCase() === "DE"
          ? "CUSTODY_LOCATION"
          : "")
  ).toUpperCase();

  const lotScopeLabel =
    lotScopeUsed === "GLOBAL_TOKEN_POOL"
      ? language === "en"
        ? "Global token pool"
        : "Globaler Token-Pool"
      : lotScopeUsed === "CUSTODY_LOCATION"
        ? language === "en"
          ? "Custody location"
          : "Verwahrort (Wallet/Konto)"
        : null;

  const itGlobalPoolNote =
    String(cc || "").toUpperCase() === "IT" && lotScopeUsed === "GLOBAL_TOKEN_POOL"
      ? language === "en"
        ? "Lot scope: Global token pool. Pool identity: One pool per asset across included custody locations. Own-wallet transfers do not change the tax lot stack."
        : "Lot-Scope: Globaler Token-Pool. Pool-Identität: Ein Pool je Asset über alle einbezogenen Verwahrorte. Eigene Transfers ändern den steuerlichen Lot-Stack nicht."
      : null;

  const createdAt = useMemo(() => {
    const locale = language === "de" ? "de-DE" : "en-US";
    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date());
  }, [language]);

  const vetRows = filterRowsForReport(
    filterUkRowsForPdf(ds.vetTaxRows || [], isUK),
    reportMode
  );

  const vthoRows = filterRowsForReport(
    filterUkRowsForPdf(ds.vthoTaxRows || [], isUK),
    reportMode
  );

  const b3trRows = filterRowsForReport(
    filterUkRowsForPdf(ds.b3trTaxRows || [], isUK),
    reportMode
  );

  const exchangeTaxRows = filterRowsForReport(
    filterUkRowsForPdf(ds.exchangeTaxRows || [], isUK),
    reportMode
  );

  const exchangeImports = filterExchangeImportsForReport(
    ds.exchangeImports || [],
    reportMode
  );

  // NFT and Liquidity are disposal-only sections.
  // For UK PDFs, never render rows that final export moved to Review.
  const liquidityRows = isIncomeReport
    ? []
    : filterUkLiquidityRowsForPdf(ds.liquidityTaxRows || [], isUK);

  const nftRows = isIncomeReport
    ? []
    : filterUkRowsForPdf(ds.nftTaxRows || [], isUK);

  const vetTotals = withReportModeTotals(ds.vetTotals || {}, reportMode);
  const vthoTotals = withReportModeTotals(ds.vthoTotals || {}, reportMode);
  const b3trTotals = withReportModeTotals(ds.b3trTotals || {}, reportMode);
  const exchangeTotals = withReportModeTotals(ds.exchangeTotals || {}, reportMode);
  const nftTotals = isIncomeReport ? withReportModeTotals({}, reportMode) : withReportModeTotals(ds.nftTotals || {}, reportMode);
  const liquidityTotals = isIncomeReport ? withReportModeTotals({}, reportMode) : withReportModeTotals(ds.liquidityTotals || {}, reportMode);

  const ukFinalExport = useMemo(() => {
    if (!isUK || !Number(finalYear)) return null;

    try {
      return buildUkFinalExport(ds, Number(finalYear), {
        fiatCurrency: "GBP",
        language,
      });
    } catch (err) {
      console.error("❌ UK PDF final export failed", err);
      return null;
    }
  }, [isUK, ds, finalYear, language]);

  const ukSections = ukFinalExport?.sectionBreakdown || null;
  const ukReviewCount =
    Number(ukFinalExport?.reviewRows?.length ?? ds?.ukTaxSummary?.reviewItemCount ?? 0) || 0;

  const printVetTotals =
    isUK && ukSections
      ? pairForReportMode(ukSections.VET, reportMode)
      : {
          swapProfit: asNumber(vetTotals.disposalNetEUR ?? vetTotals.disposalNetFiat),
          otherIncome: asNumber(vetTotals.otherIncomeEUR ?? vetTotals.otherIncomeFiat),
          total: asNumber(vetTotals.totalTaxableEUR ?? vetTotals.totalTaxableFiat),
        };

  const printVthoTotals =
    isUK && ukSections
      ? pairForReportMode(ukSections.VTHO, reportMode)
      : {
          swapProfit: asNumber(vthoTotals.disposalNetEUR ?? vthoTotals.disposalNetFiat),
          otherIncome: asNumber(vthoTotals.otherIncomeEUR ?? vthoTotals.otherIncomeFiat),
          total: asNumber(vthoTotals.totalTaxableEUR ?? vthoTotals.totalTaxableFiat),
        };

  const printB3trTotals =
    isUK && ukSections
      ? pairForReportMode(ukSections["VIP-180"], reportMode)
      : {
          swapProfit: asNumber(b3trTotals.disposalNetEUR ?? b3trTotals.disposalNetFiat),
          otherIncome: asNumber(b3trTotals.otherIncomeEUR ?? b3trTotals.otherIncomeFiat),
          total: asNumber(b3trTotals.totalTaxableEUR ?? b3trTotals.totalTaxableFiat),
        };

  const printExchangeTotals =
    isUK && ukSections
      ? pairForReportMode(ukSections.EXCHANGE, reportMode)
      : {
          swapProfit: asNumber(exchangeTotals?.disposalNetEUR ?? exchangeTotals?.disposalNetFiat),
          otherIncome: asNumber(exchangeTotals?.otherIncomeEUR ?? exchangeTotals?.otherIncomeFiat),
          total: asNumber(exchangeTotals?.totalTaxableEUR ?? exchangeTotals?.totalTaxableFiat),
        };

  const printLiquidityTotals =
    isUK && ukSections
      ? pairForReportMode(ukSections.LIQUIDITY, reportMode)
      : {
          swapProfit: asNumber(
            liquidityTotals.disposalNetEUR ??
              liquidityTotals.disposalProfitEUR ??
              liquidityTotals.disposalNetFiat ??
              liquidityTotals.disposalProfitFiat
          ),
          otherIncome: 0,
          total: asNumber(
            liquidityTotals.disposalNetEUR ??
              liquidityTotals.disposalProfitEUR ??
              liquidityTotals.disposalNetFiat ??
              liquidityTotals.disposalProfitFiat
          ),
        };

  const printNftTotal =
    isUK && ukSections
      ? asNumber(pairForReportMode(ukSections.NFT, reportMode).swapProfit)
      : asNumber(
          nftTotals.disposalEUR ??
            nftTotals.disposalFiat ??
            nftTotals.disposalNetEUR ??
            nftTotals.disposalNetFiat
        );

  const hasExchange = exchangeImports.length > 0 || exchangeTaxRows.length > 0;
  const hasVet = vetRows.length > 0;
  const hasVtho = vthoRows.length > 0;
  const hasB3tr = b3trRows.length > 0;
  const hasLiquidity = !isIncomeReport && liquidityRows.length > 0;
  const hasNft = !isIncomeReport && nftRows.length > 0;
  const hasRaw = rawVetVthoTxs.length > 0 || rawVip180Txs.length > 0;

  const mainWallets = walletSet?.mainAddresses || walletSet?.addresses || [];
  const delegateWallets = walletSet?.delegateAddresses || [];

  const setIdValue = datasetId || null;
  const documentCode = `${finalYear || "-"}-${reportSuffix}`;
  const reportFacts = [
    ...(setIdValue ? [{ label: "Set-ID", value: setIdValue }] : []),
    {
      label: language === "en" ? "Document code" : "Dokumentcode",
      value: documentCode,
    },
    {
      label: language === "en" ? "Created" : "Erstellt",
      value: createdAt,
    },
    {
      label: language === "en" ? "Tax year" : "Steuerjahr",
      value: String(displayTaxYear || "-"),
    },
    ...(taxYearPeriod
      ? [
          {
            label: language === "en" ? "Reporting period" : "Berichtszeitraum",
            value: `${fmtUtcDate(taxYearPeriod.startTs)} – ${fmtUtcDate(taxYearPeriod.endInclusiveTs)}`,
          },
        ]
      : []),
    ...(partialYearNotice
      ? [
          {
            label: language === "en" ? "Data as of" : "Datenstand",
            value: dataAsOfDate,
          },
          {
            label: language === "en" ? "Report status" : "Berichtsstatus",
            value:
              language === "en"
                ? "Provisional (open tax year)"
                : "Vorläufig (laufendes Steuerjahr)",
          },
        ]
      : []),
  ];

  const parameterFacts = [
    { label: language === "en" ? "Country" : "Land", value: cc },
    { label: language === "en" ? "Method" : "Methode", value: methodUsed },
    ...(lotScopeLabel
      ? [
          {
            label: language === "en" ? "Lot scope" : "Lot-Scope",
            value: lotScopeLabel,
          },
        ]
      : []),
    {
      label: language === "en" ? "Report currency" : "Berichtswährung",
      value: currencyUsed,
    },
    ...(isUK
      ? [
          {
            label: language === "en" ? "UK review items" : "UK Review-Positionen",
            value:
              ukReviewCount > 0
                ? `${ukReviewCount} excluded from tax totals`
                : "0",
          },
        ]
      : []),
    {
      label: language === "en" ? "Pricing methodology" : "Kursmethodik",
      value: pricingMethodologyHeader || priceSourceLabel,
    },
    {
      label: language === "en" ? "Tax year" : "Steuerjahr",
      value: String(displayTaxYear || "-"),
    },
    ...(taxYearPeriod
      ? [
          {
            label: language === "en" ? "Reporting period" : "Berichtszeitraum",
            value: `${fmtUtcDate(taxYearPeriod.startTs)} – ${fmtUtcDate(taxYearPeriod.endInclusiveTs)}`,
          },
        ]
      : []),
  ];

  const printReadySignature = [
    pdfVariant,
    finalYear,
    cc,
    reportMode,
    vetRows.length,
    vthoRows.length,
    b3trRows.length,
    exchangeImports.length,
    exchangeTaxRows.length,
    liquidityRows.length,
    nftRows.length,
    rawVetVthoTxs.length,
    rawVip180Txs.length,
  ].join("|");

  const expectDetailTables =
    (isCalcLike &&
      (hasExchange || hasVet || hasVtho || hasB3tr || hasLiquidity || hasNft)) ||
    (pdfVariant === "raw" && hasRaw);

  useLayoutEffect(() => {
    const root = document.getElementById("tax-report-print-root");
    if (!root) return;

    root.setAttribute("data-v3tax-print-ready", "0");

    let cancelled = false;
    let stableFrames = 0;
    let lastRowCount = -1;
    let framesSeen = 0;

    const finishWhenStable = () => {
      if (cancelled) return;
      framesSeen += 1;

      const rowCount = root.querySelectorAll(
        "tbody tr, .tr-section table tbody tr"
      ).length;

      if (rowCount === lastRowCount && rowCount > 0) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
        lastRowCount = rowCount;
      }

      const minStableFrames = expectDetailTables ? 3 : 2;
      const hasEnoughRows = rowCount > 0;
      const layoutSettled =
        hasEnoughRows && stableFrames >= minStableFrames;

      // Leerer Report (nur Cover/Dashboard): nach kurzer Stabilisierung drucken
      const emptyReportReady =
        !expectDetailTables && framesSeen >= minStableFrames;

      if (layoutSettled || emptyReportReady) {
        root.setAttribute("data-v3tax-print-ready", "1");
        return;
      }

      requestAnimationFrame(finishWhenStable);
    };

    requestAnimationFrame(finishWhenStable);

    return () => {
      cancelled = true;
      root.setAttribute("data-v3tax-print-ready", "0");
    };
  }, [printReadySignature, expectDetailTables]);

  return (
    <div id="tax-report-print-root" aria-hidden="true">
      <div className="tax-report">
        {isCalcLike && (
          <section className="tax-report__page1">
            <header className="tr-header tr-cover">
              <div className="tr-cover-top">
                <div className="tr-cover-main">
                  <div className="tr-kicker">V3Tax</div>
                  <h1 className="tr-title">{reportTitle}</h1>
                  <div className="tr-subtitle">{reportSubtitle}</div>
                  <p className="tr-cover-lead">{coverLead}</p>
                  {partialYearNotice ? (
                    <p className="tr-cover-lead tr-cover-lead--warn">{partialYearNotice}</p>
                  ) : null}
                </div>
                <aside className="tr-docbox" aria-label="Report information">
                  <div className="tr-docbox__title">
                    {language === "en" ? "Report information" : "Berichtsdaten"}
                  </div>
                  <FactRows rows={reportFacts} />
                </aside>
              </div>
              <div className="tr-cover-grid">
                <section className="tr-panel">
                  <div className="tr-panel__title">
                    {language === "en" ? "Parameters" : "Parameter"}
                  </div>
                  <FactRows rows={parameterFacts} />
                </section>
                <section className="tr-panel">
                  <div className="tr-panel__title">
                    {language === "en" ? "Wallet scope" : "Wallet-Umfang"}
                  </div>
                  <div className="tr-wallet-group">
                    <div className="tr-wallet-group__label">
                      {language === "en" ? "Main addresses" : "Hauptadressen"}
                    </div>
                    <ul className="tr-wallets">
                      {mainWallets.length > 0 ? (
                        mainWallets.map((a) => (
                          <li key={`main-${a}`}>
                            <code>{a}</code>
                          </li>
                        ))
                      ) : (
                        <li>-</li>
                      )}
                    </ul>
                  </div>
                  {delegateWallets.length > 0 && (
                    <div className="tr-wallet-group tr-wallet-group--delegates">
                      <div className="tr-wallet-group__label">Delegates</div>
                      <ul className="tr-wallets">
                        {delegateWallets.map((a) => (
                          <li key={`delegate-${a}`}>
                            <code>{a}</code>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </section>
              </div>
              <div className="tr-note tr-note--soft">
                {language === "en"
                  ? "Note: Individual line items may be rounded for readability. Totals are calculated from unrounded values and rounded only at the end."
                  : "Hinweis: Einzelwerte können aus Darstellungsgründen gerundet sein. Summen werden aus ungerundeten Werten berechnet und erst am Ende gerundet."}
              </div>
            </header>

            <div id="pdf-dashboard">
              <PrintReportDashboard
                language={language}
                selectedCountry={cc}
                selectedYear={finalYear}
                displayTaxYear={displayTaxYear}
                vetTotalProfit={printVetTotals}
                vthoTotalProfit={printVthoTotals}
                b3trTotalProfit={printB3trTotals}
                nftTotalProfit={printNftTotal}
                exchangeTotalProfit={printExchangeTotals}
                liquidityTotalProfit={printLiquidityTotals}
                reportMode={reportMode}
              />
            </div>

            <section className="tr-legal tr-legal--muted">
              <div className="tr-legal__title">
                {language === "en"
                  ? "Legal Disclaimer"
                  : "Hinweis zur steuerlichen Verwendung"}
              </div>
              <p className="tr-legal__text">
                {String(cc || "").toUpperCase() === "US" || language === "en" ? (
                  <>
                    This report is provided for informational record-keeping
                    purposes and does not constitute legal or tax advice. Tax
                    treatment may depend on the user's individual circumstances
                    and applicable reporting requirements.
                    {usWalletMethodNote ? (
                      <>
                        {" "}
                        {usWalletMethodNote}
                      </>
                    ) : null}
                    {itGlobalPoolNote ? (
                      <>
                        {" "}
                        {itGlobalPoolNote}
                      </>
                    ) : null}
                  </>
                ) : String(cc || "").toUpperCase() === "IT" ? (
                  <>
                    Questo report è fornito a scopo informativo e di
                    documentazione e non costituisce consulenza legale o
                    fiscale. Il trattamento fiscale può dipendere dalla
                    situazione individuale dell'utente e dagli obblighi di
                    dichiarazione applicabili.
                  </>
                ) : isUK ? (
                  <>
                    This report is provided for informational record-keeping
                    purposes and does not constitute legal or tax advice. Tax
                    treatment may depend on the user's individual circumstances
                    and applicable UK reporting requirements.
                  </>
                ) : (
                  <>
                    Dieser Bericht dient ausschließlich der privaten Vorbereitung
                    auf Ihre persönliche Steuererklärung und ersetzt keine
                    steuerliche Beratung im Sinne des § 3 StBerG
                    (Steuerberatungsgesetz). Die Berechnungen beruhen auf den in
                    der Anwendung gesetzten Parametern und den vorliegenden
                    Transaktionsdaten. Jegliche Haftung für Vollständigkeit oder
                    Richtigkeit wird ausgeschlossen.
                    {usWalletMethodNote ? (
                      <>
                        {" "}
                        {usWalletMethodNote}
                      </>
                    ) : null}
                    {itGlobalPoolNote ? (
                      <>
                        {" "}
                        {itGlobalPoolNote}
                      </>
                    ) : null}
                  </>
                )}
              </p>
              {pricingMethodologyNote ? (
                <p className="tr-legal__text tr-pricing-note" style={{ marginTop: 8, opacity: 0.9 }}>
                  {pricingMethodologyNote}
                </p>
              ) : null}
            </section>
          </section>
        )}

        {pdfVariant === "raw" && (
          <section className="tax-report__page1">
            <header className="tr-header tr-cover">
              <div className="tr-cover-top">
                <div className="tr-cover-main">
                  <div className="tr-kicker">V3Tax</div>
                  <h1 className="tr-title">{reportTitle}</h1>
                  <div className="tr-subtitle">{reportSubtitle}</div>
                  <p className="tr-cover-lead">{coverLead}</p>
                  {partialYearNotice ? (
                    <p className="tr-cover-lead tr-cover-lead--warn">{partialYearNotice}</p>
                  ) : null}
                </div>
                <aside className="tr-docbox" aria-label="Report information">
                  <div className="tr-docbox__title">
                    {language === "en" ? "Report information" : "Berichtsdaten"}
                  </div>
                  <FactRows rows={reportFacts} />
                </aside>
              </div>
              <div className="tr-cover-grid">
                <section className="tr-panel">
                  <div className="tr-panel__title">
                    {language === "en" ? "Parameters" : "Parameter"}
                  </div>
                  <FactRows rows={parameterFacts} />
                </section>
                <section className="tr-panel">
                  <div className="tr-panel__title">
                    {language === "en" ? "Wallet scope" : "Wallet-Umfang"}
                  </div>
                  <div className="tr-wallet-group">
                    <div className="tr-wallet-group__label">
                      {language === "en" ? "Main addresses" : "Hauptadressen"}
                    </div>
                    <ul className="tr-wallets">
                      {mainWallets.length > 0 ? (
                        mainWallets.map((a) => (
                          <li key={`main-${a}`}>
                            <code>{a}</code>
                          </li>
                        ))
                      ) : (
                        <li>-</li>
                      )}
                    </ul>
                  </div>
                  {delegateWallets.length > 0 && (
                    <div className="tr-wallet-group tr-wallet-group--delegates">
                      <div className="tr-wallet-group__label">Delegates</div>
                      <ul className="tr-wallets">
                        {delegateWallets.map((a) => (
                          <li key={`delegate-${a}`}>
                            <code>{a}</code>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </section>
              </div>
              <div className="tr-note tr-note--soft">
                {language === "en"
                  ? "Classified ledger appendix. This section contains on-chain transfers and protocol reconstruction events used in the calculation pipeline."
                  : "Klassifizierte Ledger-Anlage. Dieser Abschnitt enthält On-chain-Transfers und rekonstruierte Protokoll-Events aus der Berechnungs-Pipeline."}
              </div>
            </header>
          </section>
        )}

        {isCalcLike && hasExchange && (
          <section className="tr-section tr-section--first">
            <ExchangeImportsTable
              datasetId={datasetId}
              year={finalYear}
              displayTaxYear={displayTaxYear}
              exchangeImports={exchangeImports}
              exchangeTaxRows={exchangeTaxRows}
              exchangeTotals={exchangeTotals}
              selectedCountry={cc}
              pdfExportMode={true}
              isPreviewMode={false}
              onDataReady={noop}
            />
          </section>
        )}

        {isCalcLike && hasVet && (
          <section
            className={`tr-section ${!hasExchange ? "tr-section--first" : ""}`}
          >
            <VetTaxTable
              datasetId={datasetId}
              year={finalYear}
              displayTaxYear={displayTaxYear}
              country={cc}
              vetDataFromProps={vetRows}
              vetTotalsFromProps={vetTotals}
              onTotalProfitCalculated={noop}
              onDataReady={noop}
              isEditMode={false}
              selectedCountry={cc}
              pdfExportMode={true}
              isPreviewMode={false}
            />
          </section>
        )}

        {isCalcLike && hasVtho && (
          <section className="tr-section">
            <VthoTaxTable
              datasetId={datasetId}
              year={finalYear}
              displayTaxYear={displayTaxYear}
              country={cc}
              vthoDataFromProps={vthoRows}
              vthoTotalsFromProps={vthoTotals}
              onTotalProfitCalculated={noop}
              onDataReady={noop}
              isEditMode={false}
              selectedCountry={cc}
              pdfExportMode={true}
              isPreviewMode={false}
            />
          </section>
        )}

        {isCalcLike && hasB3tr && (
          <section className="tr-section">
            <B3trTaxTable
              year={finalYear}
              displayTaxYear={displayTaxYear}
              b3trDataFromProps={b3trRows}
              b3trTotalsFromProps={b3trTotals}
              onTotalProfitCalculated={noop}
              onDataReady={noop}
              isEditMode={false}
              selectedCountry={cc}
              pdfExportMode={true}
              isPreviewMode={false}
            />
          </section>
        )}

        {isCalcLike && hasLiquidity && (
          <section className="tr-section">
            <LiquidityTaxTable
              year={finalYear}
              displayTaxYear={displayTaxYear}
              liquidityDataFromProps={liquidityRows}
              liquidityTotalsFromProps={liquidityTotals}
              onTotalProfitCalculated={noop}
              onDataReady={noop}
              selectedCountry={cc}
              pdfExportMode={true}
              isPreviewMode={false}
            />
          </section>
        )}

        {isCalcLike && hasNft && (
          <section className="tr-section">
            <NftTaxTable
              year={finalYear}
              displayTaxYear={displayTaxYear}
              nftDataFromProps={nftRows}
              nftTotalsFromProps={nftTotals}
              onTotalProfitCalculated={noop}
              onDataReady={noop}
              isEditMode={false}
              selectedCountry={cc}
              nonTaxableSales={nonTaxableSales}
              onNonTaxableSaleChange={handleNonTaxableSaleChange}
              onManualNftDataChange={handleManualNftDataChange}
              manualNftData={manualNftData}
              pdfExportMode={true}
              isPreviewMode={false}
            />
          </section>
        )}

        {pdfVariant === "raw" && hasRaw && (
          <section className="tr-section tr-section--first">
            <RawTransfersTable
              year={finalYear}
              displayTaxYear={displayTaxYear}
              selectedCountry={cc}
              vetVthoTxs={rawVetVthoTxs}
              vip180Txs={rawVip180Txs}
              manualClassifications={manualClassifications}
              pdfExportMode={true}
              pdfExportVariant="raw"
            />
          </section>
        )}

      </div>
    </div>
  );
}
